import type { UIMessage } from 'ai'
import type { MediaSource, UIChatMessage } from 'models'

type MessagePart = UIMessage['parts'][number]

export function isTextPart(
    part?: MessagePart | null | undefined,
): part is Extract<MessagePart, { type: 'text' }> {
    if (!part) {
        return false
    }
    return part.type === 'text'
}

export function isToolPart(
    part?: MessagePart | null | undefined,
): part is Extract<MessagePart, { type: `tool-${string}` }> {
    if (!part) {
        return false
    }
    return part.type.startsWith('tool-')
}

export function getToolPartName(part?: MessagePart): string {
    if (!isToolPart(part)) {
        return ''
    }
    return part.type.slice('tool-'.length)
}

export function isFilePart(
    part?: MessagePart | null | undefined,
): part is Extract<MessagePart, { type: 'file' }> {
    if (!part) {
        return false
    }
    return part.type === 'file'
}

/**
 * Returns the persisted `MediaSource` carried by a file part, when present.
 * Uploaded attachments stash the full upload metadata (size, extension,
 * storage handle…) under `providerMetadata['8bot'].media`, which survives
 * persistence — so saved messages can surface size/details without a schema
 * change or extra request.
 */
export function getFilePartMedia(
    part?: MessagePart | null | undefined,
): MediaSource | undefined {
    if (!isFilePart(part)) {
        return undefined
    }
    const container = part.providerMetadata?.['8bot'] as
        { media?: unknown } | undefined
    const media = container?.media
    return media && typeof media === 'object' && !Array.isArray(media)
        ? (media as MediaSource)
        : undefined
}

export function isReasoningPart(
    part?: MessagePart | null | undefined,
): part is Extract<MessagePart, { type: 'reasoning' }> {
    if (!part) {
        return false
    }
    return part.type === 'reasoning'
}

export function hasPartText(
    part?: MessagePart | null | undefined,
): part is Extract<MessagePart, { text: string }> {
    if (!part) {
        return false
    }
    if (isTextPart(part)) {
        return true
    }
    return (
        'text' in part &&
        part.text !== undefined &&
        part.text !== null &&
        part.text !== ''
    )
}

export function hasTextPart(
    message?: UIMessage | UIChatMessage | null | undefined,
): boolean {
    if (!message) {
        return false
    }
    return (message as UIMessage).parts.some((part) => isTextPart(part))
}

export function getTextPart(
    message?: UIMessage | UIChatMessage | null | undefined,
): string | undefined {
    if (!message) {
        return undefined
    }
    const parts = (message as UIMessage).parts
    if (!Array.isArray(parts)) {
        return undefined
    }
    const textPart = parts.find((part) => isTextPart(part))
    return textPart?.text
}

export function getMessageText(
    message?: UIMessage | UIChatMessage | null | undefined,
): string {
    if (!message) {
        return ''
    }
    const parts = (message as UIMessage).parts
    if (!Array.isArray(parts)) {
        return ''
    }
    return parts
        .filter((p): p is Extract<MessagePart, { type: 'text' }> =>
            isTextPart(p),
        )
        .map((p) => p.text)
        .join('')
}
